import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import {formatCurrency} from "../../../helper/currencyFormatter.helper";

interface ProceedsRow {
  id: number;
  purpose: string;
  amount: string;
}

interface ProceedsDialogProps {
  open: boolean;
  onClose: () => void;
  loanName: string;
  loanSize: number;
  payoffValue: number;
  proceeds: { purpose: string; amount: number }[];
  onSave: (proceeds: { purpose: string; amount: number }[]) => Promise<void>;
  saving?: boolean;
}

const ProceedsDialog: React.FC<ProceedsDialogProps> = ({
  open,
  onClose,
  loanName,
  loanSize,
  payoffValue,
  proceeds,
  onSave,
  saving = false,
}) => {
  const [rows, setRows] = useState<ProceedsRow[]>(
    proceeds.length > 0
      ? proceeds.map((p, i) => ({ id: i + 1, purpose: p.purpose, amount: String(p.amount) }))
      : [{ id: 1, purpose: "", amount: "" }]
  );
  type Errors = Record<number, Record<string, string>>;

   const [errors, setErrors] = useState<Errors>({});
   const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

   useEffect(() => {
     setRows(
       proceeds.length > 0
         ? proceeds.map((p, i) => ({ id: i + 1, purpose: p.purpose, amount: String(p.amount) }))
         : [{ id: 1, purpose: "", amount: "" }]
     );
   }, [proceeds]);

   const handleChange = (id: number, field: "purpose" | "amount", value: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
    setErrors((prev: Record<number, Record<string, string>>) => ({
      ...prev,
      [id]: { ...prev[id], [field]: "" },
    }));
  };

   const addRow = () => {
     const newId = Math.max(...rows.map((r) => r.id), 0) + 1;
     setRows((prev) => [...prev, { id: newId, purpose: "", amount: "" }]);
   };

  const removeRow = (id: number) => {
    if (rows.length > 1) {
      setRows((prev) => prev.filter((row) => row.id !== id));
      setErrors((prev: Record<number, Record<string, string>>) => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  const validateAndSave = async () => {
    const newErrors: Record<number, Record<string, string>> = {};
    let hasErrors = false;

    const validRows = rows.filter((r) => r.purpose?.trim() && r.amount?.trim());
    const proceedsTotal = validRows.reduce((sum, r) => sum + Number(r.amount), 0);
    const total = Number(payoffValue) + Number(proceedsTotal);

    if (total > loanSize) {
      setSnackbar({
        open: true,
        message: `Payoff Value + Proceeds (${formatCurrency(total)}) cannot exceed Loan Size (${formatCurrency(loanSize)})`,
        severity: "error",
      });
      return;
    }

    validRows.forEach((r) => {
      if (!r.purpose?.trim()) {
        newErrors[r.id] = { ...newErrors[r.id], purpose: "Purpose is required" };
        hasErrors = true;
      }
      if (!r.amount?.trim()) {
        newErrors[r.id] = { ...newErrors[r.id], amount: "Amount is required" };
        hasErrors = true;
      } else if (isNaN(Number(r.amount))) {
        newErrors[r.id] = { ...newErrors[r.id], amount: "Must be a valid number" };
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    const proceedsList = validRows.map((r) => ({
      purpose: r.purpose.trim(),
      amount: Number(r.amount),
    }));

    try {
      await onSave(proceedsList);
      setSnackbar({ open: true, message: "Proceeds saved successfully!", severity: "success" });
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      const message = error?.message || "Failed to save proceeds. Please try again.";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    }
  };

  const validRows = rows.filter((r) => r.purpose?.trim() && r.amount?.trim());
  const proceedsTotal = validRows.reduce((sum, r) => sum + Number(r.amount), 0);
  const total = Number(payoffValue) + Number(proceedsTotal);
  const remaining = Number(loanSize) - Number(total);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Proceeds Details
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loan: {loanName}
        </Typography>
        <Box sx={{ mb: 2, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 4 }}>
              <Typography variant="caption" color="text.secondary">Loan Size</Typography>
              <Typography fontWeight={600}>{formatCurrency(loanSize)}</Typography>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Typography variant="caption" color="text.secondary">Payoff Value</Typography>
              <Typography fontWeight={600}>{formatCurrency(payoffValue)}</Typography>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Typography variant="caption" color="text.secondary">Remaining</Typography>
              <Typography fontWeight={600} color={remaining < 0 ? "error" : "success.main"}>
                {formatCurrency(remaining)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Purpose</TableCell>
                <TableCell align="right">Amount ($)</TableCell>
                <TableCell align="right" sx={{ width: 60 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={row.purpose}
                      onChange={(e) => handleChange(row.id, "purpose", e.target.value)}
                      placeholder="Enter purpose"
                      error={!!errors[row.id]?.purpose}
                      helperText={errors[row.id]?.purpose}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={row.amount}
                      onChange={(e) => handleChange(row.id, "amount", e.target.value)}
                      placeholder="Enter amount"
                      error={!!errors[row.id]?.amount}
                      helperText={errors[row.id]?.amount}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => removeRow(row.id)} disabled={rows.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Button startIcon={<AddIcon />} onClick={addRow} sx={{ mt: 1 }} size="small">
          Add Row
        </Button>
         <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
           <Button variant="outlined" onClick={onClose} disabled={saving} sx={{ borderRadius: 2, fontWeight: 600 }} color="error">
             Cancel
           </Button>
           <Button
             variant="contained"
             startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
             onClick={validateAndSave}
             disabled={saving}
             sx={{ borderRadius: 2, fontWeight: 600 }}
           >
             {saving ? "Saving..." : "Save"}
           </Button>
         </Box>
      </DialogContent>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ProceedsDialog;